import { Injectable, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { SocketMiddlewareService } from './socket-middleware.service';
import { ShipyardEvents } from 'icreate/common/io-event/websocket-message';
import { Shipyard } from 'icreate/common/models/shipyard';
import { ShipyardService } from './shipyard.service';
import { AccessRequestSocketService } from './access-request-socket.service';
import { AccessRequest } from 'icreate/common/models/access-request';

@Injectable({
    providedIn: 'root',
})
export class ShipyardSocketService implements OnDestroy {
    private destroySubscriptions$: Subject<void> = new Subject();

    constructor(
        private socketService: SocketMiddlewareService,
        private accessRequestSocketService: AccessRequestSocketService,
        private shipyardService: ShipyardService
    ) {}

    ngOnDestroy(): void {
        this.destroySubscriptions();
    }

    private newShipyard$() {
        return this.socketService.receiveMessageObserver(ShipyardEvents.CREATE_SHIPYARD);
    }

    private deleteShipyard$() {
        return this.socketService.receiveMessageObserver(ShipyardEvents.DELETE_SHIPYARD);
    }

    private udpateShipyard$() {
        return this.socketService.receiveMessageObserver(ShipyardEvents.UPDATE_SHIPYARD);
    }

    startSubscriptions() {
        this.destroySubscriptions();
        this.destroySubscriptions$ = new Subject();

        this.newShipyard$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (shipyard: Shipyard) => {
                const shipyardList: Shipyard[] = await this.shipyardService.getList();
                shipyardList.push({
                    id: shipyard.id,
                    name: shipyard.name,
                    iApiCompanyId: shipyard.iApiCompanyId,
                    vessels: shipyard.vessels ? shipyard.vessels : [],
                });
                this.shipyardService.updateShipyardList(shipyardList);
            });

        this.deleteShipyard$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (shipyardId: string) => {
                const shipyardList: Shipyard[] = await this.shipyardService.getList();
                shipyardList.forEach((shipyard, index) => {
                    if (shipyard.id === shipyardId) {
                        shipyardList.splice(index, 1);
                    }
                });
                this.shipyardService.updateShipyardList(shipyardList);
            });

        this.udpateShipyard$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (updatedShipyard: Shipyard) => {
                const shipyardList: Shipyard[] = await this.shipyardService.getList();
                shipyardList.forEach((shipyard, index) => {
                    if (shipyard.id === updatedShipyard.id) {
                        const vessels = shipyard.vessels;
                        shipyardList[index] = updatedShipyard;
                        shipyardList[index].vessels = vessels;
                    }
                });
                this.shipyardService.updateShipyardList(shipyardList);
            });

        this.accessRequestSocketService
            .startedEditing$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (accessRequest: AccessRequest) => {
                const shipyardList: Shipyard[] = await this.shipyardService.getList();
                shipyardList.forEach(shipyard => {
                    const vessels = shipyard.vessels;
                    if (vessels?.length > 0) {
                        const vessel = vessels.find(vessel => vessel.id === accessRequest.vesselId);
                        if (vessel) {
                            let found = false;
                            if (vessel.VesselAccessRequests?.length > 0) {
                                vessel.VesselAccessRequests.forEach((request, index) => {
                                    if (request.id == accessRequest.id) {
                                        found = true;
                                        vessel.VesselAccessRequests[index] = accessRequest;
                                    }
                                });
                            }
                            if (!found) {
                                vessel.VesselAccessRequests.push(accessRequest);
                            }
                            vessel.VesselAccessRequests = [
                                ...new Map(vessel.VesselAccessRequests.map(item => [item.id, item])).values(),
                            ];
                        }
                    }
                });
                this.shipyardService.updateShipyardList(shipyardList);
            });

        this.accessRequestSocketService
            .finishedEditing$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (accessRequest: AccessRequest) => {
                const shipyardList: Shipyard[] = await this.shipyardService.getList();
                shipyardList.forEach(shipyard => {
                    const vessels = shipyard.vessels;
                    if (vessels?.length > 0) {
                        const vessel = vessels.find(vessel => vessel.id === accessRequest.vesselId);
                        if (vessel) {
                            vessel.VesselAccessRequests = vessel.VesselAccessRequests?.filter(
                                access => access.id !== accessRequest.id
                            );
                            vessel.VesselAccessRequests = [
                                ...new Map(vessel.VesselAccessRequests.map(item => [item.id, item])).values(),
                            ];
                        }
                    }
                });
                this.shipyardService.updateShipyardList(shipyardList);
            });

        this.accessRequestSocketService
            .requestCreated$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (accessRequest: AccessRequest) => {
                const shipyardList: Shipyard[] = await this.shipyardService.getList();
                shipyardList.forEach(shipyard => {
                    const vessels = shipyard.vessels;
                    if (vessels?.length > 0) {
                        const vessel = vessels.find(vessel => vessel.id === accessRequest.vesselId);
                        if (vessel) {
                            vessel.VesselAccessRequests.push(accessRequest);
                            let found = vessel.VesselAccessRequests.find(access => access.id === accessRequest.id);
                            if (found) {
                                vessel.VesselAccessRequests.push(accessRequest);
                            }
                            vessel.VesselAccessRequests = [
                                ...new Map(vessel.VesselAccessRequests.map(item => [item.id, item])).values(),
                            ];
                        }
                    }
                });
                this.shipyardService.updateShipyardList(shipyardList);
            });
    }

    destroySubscriptions() {
        this.destroySubscriptions$.next();
        this.destroySubscriptions$.complete();
    }
}
